import { useNavigate } from 'react-router';
import { Keyboard, LogOut, Palette, UserRound, Library } from 'lucide-react';
import { useAuth } from '@/lib/auth';
import { fullName } from '@/lib/format';
import { useI18n } from '@/lib/i18n';
import { Avatar } from '@/components/ui/misc';
import { Menu, type MenuEntry } from '@/components/ui/Menu';
import { env } from '@/lib/env';

export function UserMenu({ onShortcuts, staff = true }: { onShortcuts?: () => void; staff?: boolean }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { t } = useI18n();
  if (!user) {
    return null;
  }
  const name = fullName(user) || user.cardNumber;
  const items: MenuEntry[] = [
    { id: 'profile', label: t('shell.profile'), icon: <UserRound />, onSelect: () => navigate(staff ? '/staff/staff-accounts' : '/account/profile') },
    ...(staff ? ([{ id: 'public', label: t('nav.publicCatalogue'), icon: <Library />, onSelect: () => navigate('/') }] as MenuEntry[]) : ([{ id: 'account', label: t('nav.account'), icon: <UserRound />, onSelect: () => navigate('/account') }] as MenuEntry[])),
    ...(staff && env.showDesignSystem ? ([{ id: 'ds', label: 'Design system', icon: <Palette />, onSelect: () => navigate('/staff/design-system') }] as MenuEntry[]) : []),
    ...(onShortcuts ? ([{ id: 'keys', label: t('shell.shortcuts'), icon: <Keyboard />, shortcut: '?', onSelect: onShortcuts }] as MenuEntry[]) : []),
    { type: 'separator' },
    { id: 'logout', label: t('nav.signOut'), icon: <LogOut />, danger: true, onSelect: () => void logout().then(() => navigate(staff ? '/staff/login' : '/')) },
  ];
  return (
    <Menu
      label={name}
      items={items}
      header={
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-ink">{name}</p>
          <p className="truncate text-xs text-ink-muted">{user.email ?? user.cardNumber}</p>
          <p className="mt-0.5 truncate text-2xs font-medium uppercase tracking-wider text-sapphire-700 dark:text-sapphire-300">{user.staffRole?.replace(/_/g, ' ') ?? user.categoryName}</p>
        </div>
      }
      trigger={
        <button type="button" aria-label={name} className="flex items-center gap-2 rounded-full p-0.5 pr-1 transition hover:bg-sunken">
          <Avatar name={name} src={user.photoUrl} size="sm" />
        </button>
      }
    />
  );
}
